import type {
    DynamicFilterGroupValue,
    DynamicFilterValue,
} from "app/pages/sales-intelligence/domain/search/types/dynamic-filter-values";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";
import { commonKeys } from "app/pages/sales-intelligence/utils/i18n/common";

import { translateOption } from "../../../components/filters/RelativeDateRangeFilter/utils";
import type { DynamicComboboxFilterOptions, DynamicNumberRangeOptions } from "../../../types/filterOptions";
import type { SearchFilterInstance } from "../../../types/searchFilterInstance";
import { resolveOptionLabel } from "../../../utils/filterOptions";
import { getFormattedRangeValue } from "../../../utils/rangeValueFormatters";
import type { DynamicGroupFilter } from "../../DynamicGroupFilter";

import type { ConditionChip, DynamicGroupFilterViewModel, SeededDraft } from "./DynamicGroupFilterViewModel";

type Deps = {
    readonly filter: DynamicGroupFilter;
    readonly value: DynamicFilterGroupValue;
};

const fieldLabelOf = (field: SearchFilterInstance): string =>
    (field.filter.options as { label?: string } | undefined)?.label ?? String(field.key);

const readFieldValue = (source: DynamicFilterGroupValue, key: string): DynamicFilterValue | undefined => source[key];

/** Option ids a condition value contributes, so pickers can disable them (enum widgets only). */
const optionIdsOf = (fieldValue?: DynamicFilterValue): readonly string[] | undefined => {
    if (fieldValue?.dataType === "enumArray") {
        return fieldValue.value;
    }

    return fieldValue?.dataType === "enum" ? [fieldValue.value] : undefined;
};

/** A field value → its display chips (bare value labels, OR-joined), per data type. */
const resolveChips = (field: SearchFilterInstance, fieldValue: DynamicFilterValue): readonly ConditionChip[] => {
    switch (fieldValue.dataType) {
        case "enum": {
            const { options } = field.filter.options as DynamicComboboxFilterOptions;

            return [{ label: resolveOptionLabel(options, fieldValue.value), id: fieldValue.value }];
        }
        case "enumArray": {
            const { options } = field.filter.options as DynamicComboboxFilterOptions;

            return fieldValue.value.map((id) => ({ label: resolveOptionLabel(options, id), id }));
        }
        case "date": {
            const { range, rangeType } = fieldValue.value;

            return range && rangeType
                ? [{ label: `${translateOption(rangeType)} ${translateOption(range)}`, id: "date" }]
                : [];
        }
        case "numberRange": {
            const { from, to } = fieldValue.value;

            if (from == null && to == null) {
                return [];
            }

            const { buckets, formatValue } = field.filter.options as DynamicNumberRangeOptions;

            return [{ label: getFormattedRangeValue(formatValue, buckets, { from, to }), id: "numberRange" }];
        }
    }
};

export const createDynamicGroupFilterViewModel = ({ filter, value }: Deps): Readonly<DynamicGroupFilterViewModel> => {
    const findMember = (key?: string): SearchFilterInstance | undefined =>
        key ? filter.getFields().find((field) => field.key === key) : undefined;

    return {
        get conditions() {
            return filter
                .getFields()
                .filter((field) => !field.filter.isValueEmpty(readFieldValue(value, field.key)))
                .map((field) => {
                    const fieldValue = readFieldValue(value, field.key);

                    return {
                        key: field.key,
                        inclusionLabelKey: commonKeys.inclusion(filter.getConditionInclusion(field.key)),
                        fieldLabel: fieldLabelOf(field),
                        valueLabels: fieldValue ? resolveChips(field, fieldValue).map((chip) => chip.label) : [],
                    };
                });
        },

        get baseFields() {
            return filter.getBaseFields().map((field) => ({ key: field.key, label: fieldLabelOf(field) }));
        },

        resolveActiveMember(selectedBaseKey, inclusion) {
            return selectedBaseKey && inclusion ? filter.getVariant(selectedBaseKey, inclusion) : undefined;
        },

        chipItems(activeMemberKey, draft) {
            const activeMember = findMember(activeMemberKey);
            const fieldValue = activeMemberKey ? readFieldValue(draft, activeMemberKey) : undefined;

            return activeMember && fieldValue ? resolveChips(activeMember, fieldValue) : [];
        },

        // Disable every value already committed on the field's conditions (both sides), except the one being edited
        // IN PLACE — so a value can't be reused into a duplicate/opposite condition. Once the inclusion toggle is
        // flipped, the edited condition is no longer the active member, so its values start blocking the opposite
        // side too.
        disabledOptionIds({ selectedBaseKey, activeMemberKey, initialFieldKey }) {
            if (!selectedBaseKey) {
                return undefined;
            }

            const usedValueIds = (side: InclusionOption): readonly string[] => {
                const variant = filter.getVariant(selectedBaseKey, side);

                if (!variant) {
                    return [];
                }

                const isEditingInPlace = variant.key === initialFieldKey && variant.key === activeMemberKey;

                return isEditingInPlace ? [] : (optionIdsOf(readFieldValue(value, variant.key)) ?? []);
            };

            return [...usedValueIds(inclusionOptionMap.includeOnly), ...usedValueIds(inclusionOptionMap.excludeOnly)];
        },

        seedDraft(initialFieldKey): SeededDraft {
            const editedValue = initialFieldKey ? value[initialFieldKey] : undefined;

            return {
                draft: initialFieldKey && editedValue ? { [initialFieldKey]: editedValue } : {},
                selectedBaseKey: initialFieldKey
                    ? (filter.getVariant(initialFieldKey, inclusionOptionMap.includeOnly)?.key ?? initialFieldKey)
                    : undefined,
                inclusion: initialFieldKey
                    ? filter.getConditionInclusion(initialFieldKey)
                    : inclusionOptionMap.includeOnly,
            };
        },

        commitActiveMember(activeMemberKey, draft, initialFieldKey) {
            const activeMember = findMember(activeMemberKey);
            const next: DynamicFilterGroupValue = { ...value };
            const activeValue = draft[activeMemberKey];

            if (initialFieldKey && initialFieldKey !== activeMemberKey) {
                delete next[initialFieldKey];
            }

            if (!activeMember || activeValue === undefined || activeMember.filter.isValueEmpty(activeValue)) {
                delete next[activeMemberKey];
            } else {
                next[activeMemberKey] = activeValue;
            }

            return next;
        },

        moveDraftValue(selectedBaseKey, previousInclusion, nextInclusion, draft) {
            const previousMember = filter.getVariant(selectedBaseKey, previousInclusion);
            const nextMember = filter.getVariant(selectedBaseKey, nextInclusion);

            if (!previousMember || !nextMember || previousMember.key === nextMember.key) {
                return undefined;
            }

            const valueToMove = draft[previousMember.key];

            if (valueToMove === undefined) {
                return undefined;
            }

            const nextDraft = { ...draft };

            delete nextDraft[previousMember.key];
            nextDraft[nextMember.key] = valueToMove;

            return nextDraft;
        },

        removeValue(activeMemberKey, draft, id) {
            const activeMember = findMember(activeMemberKey);
            const fieldValue = readFieldValue(draft, activeMemberKey);

            if (!activeMember || !fieldValue) {
                return draft;
            }

            const nextRaw =
                fieldValue.dataType === "enumArray" ? fieldValue.value.filter((valueId) => valueId !== id) : undefined;

            return filter.writeField(draft, activeMember, nextRaw);
        },
    };
};
